import {createSlice} from '@reduxjs/toolkit';

import type {GetEventAttributionListMeta} from 'api/events/events.types';
import {
    clearAppliedFilters,
    getEventFilterOptions,
    getPaginatedPastEventListData,
    getSearchedPastEventsListData,
    setEventSearchText,
    updateAppliedFilters,
} from 'pages/event-management/store/past-events-list-actions';
import {eventsAdapter} from 'pages/event-management/store/past-event-list-adapters';
import type {EventFilterOption} from 'pages/event-management/utils/event-management.types';
import dayjs from 'utils/dayjs';

const initialFromDate = dayjs('01/01/2024').startOf('day');
const initialToDate = dayjs();

export interface PastEventsListState {
    appliedFilters: {
        eventTypeIds?: number[];
        fromDate: dayjs.Dayjs;
        searchText?: string;
        toDate: dayjs.Dayjs;
        venueIds?: number[];
    };
    events: ReturnType<typeof eventsAdapter.getInitialState>;
    filters: {
        eventTypeIds?: EventFilterOption[];
        isLoading: boolean;
        venueIds?: EventFilterOption[];
    };
    isLoading: boolean;
    meta: GetEventAttributionListMeta;
}

export const initialState: PastEventsListState = {
    appliedFilters: {
        eventTypeIds: [],
        fromDate: initialFromDate,
        searchText: '',
        toDate: initialToDate,
        venueIds: [],
    },
    events: eventsAdapter.getInitialState({
        isLoading: false,
    }),
    filters: {
        eventTypeIds: [],
        isLoading: false,
        venueIds: [],
    },
    isLoading: false,
    meta: {
        limit: 25,
        nextOffset: 25,
        offset: 0,
        totalCount: 25,
        totalFilteredCount: 25,
    },
};

const pastEventsListSlice = createSlice({
    name: 'events/pastEventsList',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getEventFilterOptions.pending, (state) => {
                state.filters.isLoading = true;
            })
            .addCase(getEventFilterOptions.fulfilled, (state, {payload}) => {
                state.filters = {
                    ...payload,
                    isLoading: false,
                };
            })
            .addCase(getPaginatedPastEventListData.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                getPaginatedPastEventListData.fulfilled,
                (state, {payload}) => {
                    const {entities, meta} = payload;

                    eventsAdapter.upsertMany(state.events, entities);
                    state.meta = meta;
                    state.isLoading = false;
                }
            )
            .addCase(
                getPaginatedPastEventListData.rejected,
                (state, action) => {
                    if (action.meta.aborted) {
                        return;
                    }

                    state.isLoading = false;
                }
            )
            .addCase(getSearchedPastEventsListData.pending, (state) => {
                state.isLoading = true;

                eventsAdapter.removeAll(state.events);
            })
            .addCase(
                getSearchedPastEventsListData.fulfilled,
                (state, {payload}) => {
                    const {entities, meta} = payload;

                    eventsAdapter.setAll(state.events, entities);
                    state.meta = meta;
                    state.isLoading = false;
                }
            )
            .addCase(
                getSearchedPastEventsListData.rejected,
                (state, action) => {
                    if (action.meta.aborted) {
                        return;
                    }

                    state.isLoading = false;
                }
            )
            .addCase(setEventSearchText, (state, {payload}) => {
                state.appliedFilters.searchText = payload;
            })
            .addCase(updateAppliedFilters, (state, {payload}) => {
                state.appliedFilters = {
                    searchText: state.appliedFilters.searchText,
                    ...payload,
                };
            })
            .addCase(clearAppliedFilters, (state) => {
                state.appliedFilters = initialState.appliedFilters;
            });
    },
});

export default pastEventsListSlice.reducer;
